import { AlertTriangle, RefreshCw } from 'lucide-react'
import { toNumber } from '@/lib/decimal'
import { formatHours } from '@/lib/format'
import { useInvoiceAction } from '@/lib/invoice-queries'
import type { Invoice } from '@/lib/invoice-types'

interface InvoiceStaleBannerProps {
  invoice: Invoice
  onGoToActivity: () => void
}

export function InvoiceStaleBanner({ invoice, onGoToActivity }: InvoiceStaleBannerProps) {
  const actions = useInvoiceAction(invoice.id)

  if (invoice.stale) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-hairline bg-status-approval/10 px-4 py-3 text-sm">
        <span className="inline-flex items-center gap-2 text-ink">
          <AlertTriangle className="h-4 w-4 text-status-approval" aria-hidden />
          Let op: uren zijn aangepast na het maken van deze factuur.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onGoToActivity} className="btn-ghost h-8 px-2.5 text-sm">
            Bekijk wijzigingen
          </button>
          {invoice.status === 'DRAFT' ? (
            <button
              type="button"
              disabled={actions.recalculate.isPending}
              onClick={() => actions.recalculate.mutate()}
              className="btn-secondary"
            >
              <RefreshCw className="h-4 w-4" />
              Herbereken
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  if (invoice.missingApprovals !== null) {
    const { items, warning } = invoice.missingApprovals
    return (
      <div className="rounded-card border border-hairline bg-status-approval/10 px-4 py-3 text-sm">
        <span className="inline-flex items-center gap-2 text-ink">
          <AlertTriangle className="h-4 w-4 text-status-approval" aria-hidden />
          {warning ?? `${items.length} ${items.length === 1 ? 'week' : 'weken'} nog niet afgesloten; die uren staan niet op deze factuur.`}
        </span>
      </div>
    )
  }

  const uninvoiced = toNumber(invoice.uninvoicedHoursInPeriod)
  if (invoice.uninvoicedHoursInPeriod !== null && uninvoiced > 0) {
    return (
      <div className="rounded-card border border-hairline bg-fill-1 px-4 py-3 text-sm text-ink-soft">
        {formatHours(uninvoiced)} ongefactureerde uren binnen deze periode liften mee naar de volgende factuur.
      </div>
    )
  }

  return null
}
